import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import ApiClient from "../../lib/apiClient";
import {
  parseDeveloperCaoLink,
  type DeveloperCaoLink,
} from "../../lib/developer-cao-deep-link";
import {
  developerCaoScoutInput,
  isDeveloperCaoScout,
} from "../../lib/developer-cao-scout";
import { createScoutService } from "../../lib/services/scouts";

export function DeveloperCaoBridge() {
  const { getAccessToken, isAuthenticated } = useAuth();
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const service = useMemo(
    () => createScoutService(new ApiClient().withAuth(getAccessToken)),
    [getAccessToken],
  );

  useEffect(() => {
    const bridge = window.electron?.onDeveloperCaoOpen;
    if (!bridge) return;
    bridge((url) => setPendingUrl(url));
    return () => window.electron?.onDeveloperCaoOpen?.(null);
  }, []);

  useEffect(() => {
    if (!pendingUrl || !isAuthenticated) return;
    const parsedLink = parseDeveloperCaoLink(pendingUrl);
    if (!parsedLink) return;
    let cancelled = false;
    async function syncDeveloperScout(link: DeveloperCaoLink) {
      const scouts = await service.list();
      const existing = scouts.find(
        (scout) =>
          isDeveloperCaoScout(scout) &&
          scout.mobileBridge?.sprint === link.sprint,
      );
      const input = developerCaoScoutInput(link);
      const scout = existing
        ? await service.update(existing.id, { ...existing, ...input })
        : await service.create(input);
      if (cancelled) return;
      setPendingUrl(null);
      window.dispatchEvent(
        new CustomEvent("fintheon:scout-created", {
          detail: { scoutId: scout.id },
        }),
      );
      window.dispatchEvent(
        new CustomEvent("fintheon:navigate-tab", {
          detail: { tab: "scouts" },
        }),
      );
    }
    syncDeveloperScout(parsedLink).catch(() => {
      if (!cancelled) setPendingUrl(null);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, pendingUrl, service]);

  return null;
}
